import React from 'react'

import { format } from 'date-fns'
import {
  HiMiniArrowTrendingDown,
  HiMiniArrowTrendingUp,
  HiOutlineSquare3Stack3D,
} from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import { Statistic } from '@/components/home/ui/Statistic'

import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { useActivePursuitCount } from '@/hooks/pursuits/useActivePursuitCount'

import type { Period } from '@/types/pursuit/Pursuit'

const getCurrentPeriodString = (period: Period) => {
  const now = new Date()
  switch (period) {
    case 'week':
      return 'this week'
    case 'month':
      return `in ${format(now, 'MMMM')}`
    case 'quarter':
      return `in Q${format(now, 'Q')}`
    case 'year':
      return `in ${format(now, 'yyyy')}`
    default:
      const exhaustiveCheck: never = period
      throw new Error(`Unknown period: ${exhaustiveCheck}`)
  }
}

const getPercentageChange = (current: number, previous: number) => {
  return Math.floor(
    ((current - previous) / (previous === 0 ? 1 : previous)) * 100,
  )
}

interface ActivePursuitStatisticsProps {
  period?: Period
}

export const ActivePursuitStatistics: React.FC<
  ActivePursuitStatisticsProps
> = ({ period = 'month' }) => {
  const { activeOrgId } = useActiveOrgId()
  const { data: activePursuitCount, isLoading } = useActivePursuitCount({
    orgId: activeOrgId,
    period,
  })

  if (!activePursuitCount || isLoading) {
    return <Statistic.Skeleton />
  }

  let percentageChange = getPercentageChange(
    activePursuitCount.current_period,
    activePursuitCount.previous_period_same_time,
  )

  return (
    <Statistic
      label={`New Pursuits ${getCurrentPeriodString(period)}`}
      value={activePursuitCount.current_period}
    >
      <Statistic.Icon icon={HiOutlineSquare3Stack3D} variant="green" />
      {!isNaN(percentageChange) && (
        <Statistic.Footer>
          <span
            className={twMerge(
              'flex items-center gap-1 text-sm font-medium',
              percentageChange > 0
                ? 'text-green-600 dark:text-green-500'
                : 'text-red-600 dark:text-red-500',
            )}
          >
            {percentageChange > 0 ? (
              <HiMiniArrowTrendingUp size={16} />
            ) : (
              <HiMiniArrowTrendingDown size={16} />
            )}
            {percentageChange}%
          </span>
          <span className="text-primary-500 dark:text-primary-400 text-[13px] ml-[6px]">
            {percentageChange >= 0
              ? `more than this time last ${period}`
              : `less than this time last ${period}`}
          </span>
        </Statistic.Footer>
      )}
    </Statistic>
  )
}

ActivePursuitStatistics.displayName = 'Home.Dashboard.ActivePursuitStatistics'
